import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';

import { getBim360AdminUrl } from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useContextWithValidation,
} from '@adsk/alloy-react-context';

import { useCurrentAccountId } from './CurrentAccountIdContext';
import { fetchWithAccountType } from './helpers/fetchWithAccountType';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';

type Services = {
  service_type: string;
  display_name: string;
  service_category: string;
  service_access: string;
  service_domain: string;
  service_icon?: string;
  account_url?: string;
};

// See https://adminv1.api.b360-dev.autodesk.com/api#!/users/InternalV1_Users_get_project_entitlements
type Account = {
  account_display_name: string;
  account_id: string;
  account_only_scope: string[];
  account_scope: string[];
  account_url: string;
  account_type?: string;
  api_access: boolean;
  image_url: string;
  region: string;
  mfaBlocked?: boolean;
  services: Services[];
};

type AccountsResponse = {
  current_account_id: string;
  accounts: Account[];
  record_count: number;
  offset: number;
  locale: string;
  next?: string;
};

type AccountEntitlementsContextValue = {
  data: null | AccountsResponse;
  error: null | unknown;
  loading: boolean;
  retry(): void;
  setAccounts(data: AccountEntitlementsContextValue['data']): void;
  updateAccount(account: Account): void;
  addAccount(account: Account): void;
};

type AccountEntitlementsProviderProps = {
  disabled?: boolean;
  children?: ReactNode;
};

/**
 * Context for account entitlements.
 */
export const AccountEntitlementsContext =
  getOrCreateGlobalContext<AccountEntitlementsContextValue>(
    CONTEXT_NAMES.AccountEntitlementsContext,
  );

export const useAccountEntitlements = () =>
  useContextWithValidation(AccountEntitlementsContext, {
    hook: 'useAccountEntitlements',
    provider: 'AccountEntitlementsProvider',
  });

/**
 * This hook returns the current Account based on the current Account entitlements response.
 * Returns undefined if we don't have Account entitlement.
 *
 * @returns {undefined|object}
 */
export const useCurrentAccount = () => {
  const accountId = useCurrentAccountId();
  const { data: accountEntitlements } = useAccountEntitlements();
  return accountEntitlements?.accounts.find(
    (account) => account?.account_id === accountId,
  );
};

const BASE_URL = `${getBim360AdminUrl()}/v1/account_entitlements?service_categories=next_gen,classic,admin&include_services=true&limit=200&offset=0`;
const getNextKey = (pageData: AccountsResponse) =>
  pageData.next ? `${getBim360AdminUrl()}/v1${pageData.next}` : '';

export const AccountEntitlementsProvider: FC<
  AccountEntitlementsProviderProps
> = ({ children, disabled }) => {
  const [data, setData] = useState<AccountsResponse | null>(null);

  useProviderOnlyOnce({
    context: AccountEntitlementsContext,
    contextName: CONTEXT_NAMES.AccountEntitlementsContext,
  });

  const {
    data: accountsPages,
    loading,
    error,
    retry,
  } = useGetAllPaginatedData<AccountsResponse>({
    url: !disabled ? BASE_URL : '',
    getNextKey,
    fetcher: fetchWithAccountType,
  });

  const metadata = accountsPages[0];
  const accounts = useMemo(() => {
    return (
      accountsPages &&
      removeOldDuplicatesBy(
        accountsPages.flatMap((page) => page.accounts),
        (item) => item.account_id,
      )
    );
  }, [accountsPages]);

  useEffect(() => {
    if (metadata) {
      setData({
        ...metadata,
        accounts,
        record_count: accounts.length,
      });
    }
  }, [accounts, metadata]);

  const value = useMemo(() => {
    const updateAccount = (account: Account) => {
      const newData = { ...data } as AccountsResponse;
      if (!newData.accounts) {
        newData.accounts = [];
      }
      newData.accounts = newData.accounts.map((p) =>
        p.account_id === account.account_id ? account : p,
      );
      setData(newData);
    };
    const addAccount = (account: Account) => {
      const newData = { ...data } as AccountsResponse;
      newData.accounts = [account, ...newData.accounts];
      setData(newData);
    };

    return {
      data,
      loading,
      error,
      retry,
      setAccounts: setData,
      updateAccount,
      addAccount,
    };
  }, [data, loading, error, retry]);

  return (
    <AccountEntitlementsContext.Provider value={value}>
      {children}
    </AccountEntitlementsContext.Provider>
  );
};

AccountEntitlementsProvider.displayName = 'AccountEntitlementsProvider';

AccountEntitlementsProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
  /** Whether we should disable this provider */
  disabled: PropTypes.bool,
};
